import {
  collection,
  doc,
  onSnapshot,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { env } from '../config/env';
import { useAuth } from '../contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { SubscriptionType } from './subscription/subscription.service';

// Configuration Stripe - Charger depuis les variables d'environnement
// Configuration Stripe - Charger depuis les variables d'environnement
const STRIPE_PUBLISHABLE_KEY = env.stripe.publishableKey || 'pk_test_your_test_key_here';

// Initialiser Stripe
let stripePromise: Promise<any> | null = null;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

// Configuration des produits et prix
export const STRIPE_PRODUCTS = {
  essential: {
    priceId: 'price_1RYBu2JTjTLaR8wtLr6wZ2BX',
    name: 'Plan Essential',
    amount: 900, // 9.00 EUR en centimes
    currency: 'eur',
    interval: 'month' as const,
    features: ['multipleEvents', 'advancedReports', 'emailSupport']
  },
  pro: {
    priceId: 'price_1RYBvWJTjTLaR8wtxbMHKfnS',
    name: 'Plan Pro',
    amount: 1900, // 19.00 EUR en centimes
    currency: 'eur',
    interval: 'month' as const,
    features: ['unlimitedEvents', 'allFeatures', 'prioritySupport']
  }
};

// Types pour Firestore
export interface StripeCustomer {
  id: string;
  email: string;
  stripeId: string;
  created: Date;
}

export interface StripeSubscription {
  id: string;
  status: 'active' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'trialing' | 'unpaid';
  priceId: string;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  canceledAt?: Date;
  trialStart?: Date;
  trialEnd?: Date;
  metadata?: { [key: string]: any };
}

export interface CheckoutSession {
  sessionId?: string;
  url?: string;
  error?: {
    type: string;
    message: string;
  };
  created: Date;
}

class FirebaseStripeService {
  private userId: string | null = null;

  constructor() {
    // Initialiser avec l'utilisateur actuel si disponible
    this.initializeUser();
  }

  private async initializeUser() {
    // Cette méthode sera appelée pour définir l'utilisateur actuel
    // Elle peut être appelée depuis un hook ou context
  }

  setUser(userId: string) {
    this.userId = userId;
  }

  /**
   * Créer un client Stripe dans Firebase
   */
  async createCustomer(userId: string, email: string): Promise<string> {
    try {
      // Vérifier si le client existe déjà
      const existingCustomer = await this.getCustomer(userId);
      if (existingCustomer) {
        return existingCustomer.id;
      }

      // Créer un document dans la collection customers avec userId comme ID
      const customerData = {
        email,
        userId,
        created: new Date()
      };

      // Utiliser setDoc avec userId comme ID au lieu d'addDoc
      const customerRef = doc(db, 'customers', userId);
      await setDoc(customerRef, customerData);

      return userId;
    } catch (error) {
      console.error('Erreur lors de la création du client Stripe:', error);
      throw error;
    }
  }

  /**
   * Récupérer un client Stripe par userId
   */
  async getCustomer(userId: string): Promise<StripeCustomer | null> {
    try {
      // Accès direct au document avec userId comme ID
      const customerRef = doc(db, 'customers', userId);
      const customerSnap = await getDoc(customerRef);

      if (!customerSnap.exists()) {
        return null;
      }

      const customerData = customerSnap.data();

      return {
        id: customerSnap.id,
        ...customerData
      } as StripeCustomer;
    } catch (error) {
      console.error('Erreur lors de la récupération du client Stripe:', error);
      return null;
    }
  }

  /**
   * Vérifier si un client Stripe existe
   */
  async checkCustomerExists(userId: string): Promise<boolean> {
    try {
      const customer = await this.getCustomer(userId);
      return customer !== null;
    } catch (error) {
      console.error('Erreur lors de la vérification du client Stripe:', error);
      return false;
    }
  }

  /**
   * Créer une session de checkout
   */
  async createCheckoutSession(
    userId: string,
    priceId: string,
    subscriptionType: SubscriptionType,
    options: {
      successUrl?: string;
      cancelUrl?: string;
      trialPeriodDays?: number;
      allowPromotionCodes?: boolean;
    } = {}
  ): Promise<{ sessionId?: string | undefined; url?: string; error?: any }> {
    try {
      // S'assurer que le client existe ou le créer automatiquement
      let customer = await this.getCustomer(userId);
      if (!customer) {
        const currentUser = auth.currentUser;
        const email = currentUser?.email || '';
        await this.createCustomer(userId, email);
      }

      // Données de la session de checkout
      const checkoutSessionData: Record<string, any> = {
        price: priceId,
        success_url: options.successUrl || `${window.location.origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: options.cancelUrl || `${window.location.origin}/subscription/cancel`,
        allow_promotion_codes: options.allowPromotionCodes ?? true,
        mode: 'subscription',
        metadata: {
          userId,
          subscriptionType,
          source: 'wedbox-app'
        },
        created: new Date()
      };

      if (options.trialPeriodDays && options.trialPeriodDays > 0) {
        checkoutSessionData.trial_period_days = options.trialPeriodDays;
      }

      // Créer le document dans Firestore
      const docRef = await addDoc(
        collection(db, `customers/${userId}/checkout_sessions`),
        checkoutSessionData
      );

      // Écouter les changements pour récupérer l'URL de redirection
      return new Promise((resolve, reject) => {
        const unsubscribe = onSnapshot(docRef, (snap) => {
          const data = snap.data() as CheckoutSession;

          if (data?.error) {
            unsubscribe();
            reject(data.error);
            return;
          }

          if (data?.url) {
            unsubscribe();
            resolve({
              sessionId: data.sessionId || undefined,
              url: data.url
            });
            return;
          }
        });

        // Timeout après 30 secondes
        setTimeout(() => {
          unsubscribe();
          reject(new Error('Checkout session creation timeout'));
        }, 30000);
      });
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Récupérer les abonnements d'un utilisateur
   */
  async getSubscriptions(userId: string): Promise<StripeSubscription[]> {
    try {
      const q = query(
        collection(db, `customers/${userId}/subscriptions`),
        orderBy('created', 'desc')
      );

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as StripeSubscription[];
    } catch (error) {
      console.error('Error getting subscriptions:', error);
      return [];
    }
  }

  /**
   * Récupérer l'abonnement actif d'un utilisateur
   */
  async getActiveSubscription(userId: string): Promise<StripeSubscription | null> {
    try {
      const subscriptions = await this.getSubscriptions(userId);

      // Trouver l'abonnement actif
      const activeSubscription = subscriptions.find(sub =>
        sub.status === 'active' || sub.status === 'trialing'
      );

      return activeSubscription || null;
    } catch (error) {
      console.error('Error getting active subscription:', error);
      return null;
    }
  }

  /**
   * Récupérer l'historique des paiements / factures d'un utilisateur
   */
  async getPayments(userId: string): Promise<any[]> {
    try {
      let querySnapshot;
      try {
        const q = query(
          collection(db, `customers/${userId}/payments`),
          orderBy('created', 'desc')
        );
        querySnapshot = await getDocs(q);
      } catch (orderError) {
        // Fallback sans orderBy si l'index Firestore n'est pas encore prêt
        querySnapshot = await getDocs(collection(db, `customers/${userId}/payments`));
      }

      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        let createdDate: Date;
        if (data.created?.toDate) {
          createdDate = data.created.toDate();
        } else if (typeof data.created === 'number') {
          createdDate = data.created < 10000000000 ? new Date(data.created * 1000) : new Date(data.created);
        } else if (data.created) {
          createdDate = new Date(data.created);
        } else {
          createdDate = new Date();
        }

        const receiptUrl = 
          data.charges?.data?.[0]?.receipt_url || 
          data.receipt_url || 
          data.hosted_invoice_url || 
          data.invoice_pdf || 
          null;

        return {
          id: doc.id,
          amount: data.amount ?? data.amount_paid ?? data.amount_received ?? 0,
          currency: data.currency || 'eur',
          status: data.status || 'succeeded',
          created: createdDate,
          receiptUrl,
          ...data
        };
      }).sort((a, b) => (b.created?.getTime() || 0) - (a.created?.getTime() || 0));
    } catch (error) {
      console.error('Error getting payments/invoices:', error);
      return [];
    }
  }

  /**
   * Créer un portal de gestion de l'abonnement
   */
  async createPortalSession(
    _userId: string,
    returnUrl?: string
  ): Promise<{ url: string }> {
    try {
      const functions = getFunctions(undefined, 'us-central1');
      const createPortalLink = httpsCallable<{ returnUrl?: string }, { url: string }>(
        functions,
        'ext-firestore-stripe-payments-createPortalLink'
      );
      
      const res = await createPortalLink({
        returnUrl: returnUrl || `${window.location.origin}/settings/subscription`
      });

      if (res.data?.url) {
        return { url: res.data.url };
      }
      throw new Error("L'URL du portail client n'a pas été retournée par Stripe.");
    } catch (error: any) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  }

  /**
   * Annuler un abonnement
   */
  async cancelSubscription(userId: string, subscriptionId: string): Promise<void> {
    try {
      const subscriptionRef = doc(db, `customers/${userId}/subscriptions`, subscriptionId);

      await updateDoc(subscriptionRef, {
        cancel_at_period_end: true,
        updated: new Date()
      });
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  /**
   * Réactiver un abonnement annulé
   */
  async reactivateSubscription(userId: string, subscriptionId: string): Promise<void> {
    try {
      const subscriptionRef = doc(db, `customers/${userId}/subscriptions`, subscriptionId);

      await updateDoc(subscriptionRef, {
        cancel_at_period_end: false,
        updated: new Date()
      });
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      throw error;
    }
  }

  /**
   * Écouter les changements d'abonnement en temps réel
   */
  subscribeToSubscriptionChanges(
    userId: string,
    callback: (subscriptions: StripeSubscription[]) => void
  ): () => void {
    const q = query(
      collection(db, `customers/${userId}/subscriptions`),
      orderBy('created', 'desc')
    );

    return onSnapshot(q, (querySnapshot) => {
      const subscriptions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as StripeSubscription[];

      callback(subscriptions);
    });
  }
}

// Instance singleton
export const firebaseStripeService = new FirebaseStripeService();
export default firebaseStripeService; 